<template>
  <div class="controls-dropdown-wrapper" v-show="isSiteActionFlags" ref="dropdownWrapper">
    <button 
      class="dropdown-trigger" 
      @click.stop="$emit('toggle-dropdown', $event)"
      @touchend.stop.prevent="$emit('toggle-dropdown', $event)"
      :class="{ active: isDropdownOpen }"
      type="button"
      ref="dropdownButton"
      :data-mobile="isMobile"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
      </svg>
      <span class="dropdown-label">{{ $t('actions') }}</span>
    </button>
    <transition name="dropdown">
      <div 
        class="dropdown-menu" 
        v-show="isDropdownOpen" 
        v-if="isDropdownOpen"
        @click.stop
      >
        <div class="dropdown-section">
          <div class="dropdown-section-title">{{ $t('device_actions') }}</div>
          <div class="dropdown-actions">
            <button 
              class="dropdown-action-btn" 
              @click="$emit('action-click', 'is_update')"
              :class="{ active: siteActionFlags.is_update }"
            >
              <RestartUpdateBtn :isUpdateBtn="true" :flag="{ name: 'is_update', isOn: siteActionFlags.is_update }" />
              <span>{{ $t('update') }}</span>
            </button>
            <button 
              class="dropdown-action-btn" 
              @click="$emit('action-click', 'is_restart')"
              :class="{ active: siteActionFlags.is_restart }"
            >
              <RestartUpdateBtn :isRestartBtn="true" :flag="{ name: 'is_restart', isOn: siteActionFlags.is_restart }" />
              <span>{{ $t('restart') }}</span>
            </button>
          </div>
        </div>
        <div class="dropdown-section" v-if="Object.keys(siteActionFlags.user_flags).length > 0">
          <div class="dropdown-section-title">{{ $t('device_settings') }}</div>
          <div class="dropdown-toggles">
            <div 
              v-for="(flagValue, flagName, idx) in siteActionFlags.user_flags" 
              :key="flagName + idx"
              class="dropdown-toggle-item"
            >
              <Tooltip :texts="flagTooltipTexts(flagName)">
                <div class="toggle-item-content">
                  <span class="toggle-label">{{ $t(flagName.split('is_')[1]) }}</span>
                  <IOSSwitchButton 
                    @toggleFlag="$emit('toggle-flag', $event)" 
                    :userFlag="{ name: flagName, isOn: flagValue }" 
                    :sensorFlag="{ name: flagName, isOn: siteActionFlags.sensor_flags[flagName] }"
                    :isDisabled="isFlagDisabled(flagName)"
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import IOSSwitchButton from '@/modules/common/components/IOSSwitchButton'
import RestartUpdateBtn from '@/modules/common/icons/RestartUpdateBtn'
import Tooltip from '@/modules/common/components/Tooltip'
import { getFlagTooltipTexts, isFlagLocked } from '../utils/deviceFlagUtils'

export default {
  name: 'DeviceActionsDropdown',
  components: { IOSSwitchButton, RestartUpdateBtn, Tooltip },
  props: {
    isSiteActionFlags: { type: Boolean, required: true },
    siteActionFlags: { type: Object, required: true },
    isMobile: { type: Boolean, required: true },
    isDropdownOpen: { type: Boolean, required: true }
  },
  methods: {
    flagTooltipTexts(flagName) {
      return getFlagTooltipTexts(flagName, (key) => this.$t(key), this.siteActionFlags.user_flags)
    },
    isFlagDisabled(flagName) {
      return isFlagLocked(flagName, this.siteActionFlags.user_flags)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.controls-dropdown-wrapper {
  position: relative;
  flex-shrink: 0;
  z-index: 1000;
  pointer-events: auto;

  @media (max-width: 820px) {
    width: 100%;
  }

  .dropdown-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: $white;
    border: 1px solid $grey-border-color;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    font-size: $font-size-small;
    font-weight: $font-weight-semi-bold;
    color: $black-blue;
    position: relative;
    z-index: 100;
    -webkit-tap-highlight-color: transparent;
    -webkit-appearance: none;
    appearance: none;

    @media (max-width: 820px) {
      width: 100%;
      justify-content: center;
      padding: 12px 16px;
      z-index: 9998 !important; // Below modal (9999)
      touch-action: manipulation;
      pointer-events: auto !important;
      position: relative;
      -webkit-user-select: none;
      user-select: none;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
      -webkit-appearance: none;
      appearance: none;
      will-change: transform;
    }

    @media (max-width: 480px) {
      padding: 10px 12px;
      font-size: 13px;
    }

    &:hover {
      background: $light-grey;
      border-color: $blue;
    }

    &:active {
      background: $light-grey;
      transform: scale(0.98);
    }

    &.active {
      background: $light-blue;
      border-color: $blue;
      color: $blue;
    }

    svg {
      width: 18px;
      height: 18px;
      color: currentColor;
      pointer-events: none;

      @media (max-width: 480px) {
        width: 16px;
        height: 16px;
      }
    }

    .dropdown-label {
      display: inline;
      margin-left: 4px;
      
      @media (max-width: 480px) {
        font-size: 13px;
      }
    }
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: $white;
    border: 1px solid $grey-border-color;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    width: 220px;
    z-index: 9998; // Below modal (9999) but above other page elements
    overflow: hidden;

    @include rtl {
      right: auto;
      left: 0;
    }

    @media (max-width: 820px) {
      right: auto;
      left: 50% !important;
      width: calc(100vw - 30px);
      max-width: none;
      position: fixed !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
      max-height: 80vh;
      overflow-y: auto;
      z-index: 9998 !important; // Below modal (9999)
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }

    @media (max-width: 480px) {
      width: calc(100vw - 20px);
      max-height: 85vh;
    }

    .dropdown-section {
      padding: 8px 0;

      &:not(:last-child) {
        border-bottom: 1px solid $grey-border-color;
      }

      .dropdown-section-title {
        padding: 0 12px 6px;
        font-size: $font-size-tiny;
        font-weight: $font-weight-bold;
        color: $dark-grey;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .dropdown-actions {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 0 6px;
      }

      .dropdown-action-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 8px 10px;
        background: transparent;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s;
        text-align: start;
        font-size: $font-size-small;
        color: $black-blue;

        &:hover {
          background: $light-grey;
        }

        &.active {
          background: $light-blue;
          color: $blue;
        }

        span {
          flex: 1;
          white-space: nowrap;
        }
      }

      .dropdown-toggles {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 0 6px;
      }

      .dropdown-toggle-item {
        .toggle-item-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 10px;
          border-radius: 4px;
          transition: background 0.2s;

          &:hover {
            background: $light-grey;
          }

          .toggle-label {
            font-size: $font-size-small;
            color: $black-blue;
            text-transform: capitalize;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-right: 8px;
          }
        }
      }
    }
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 820px) {
  .dropdown-enter-active .dropdown-menu,
  .dropdown-leave-active .dropdown-menu {
    display: block !important;
  }
  
  .dropdown-enter .dropdown-menu,
  .dropdown-leave-to .dropdown-menu {
    display: none !important;
  }
}
</style>

